<template>
  <div class="page-wrapper">
    <div class="client-form">
      <header class="top">
        <h2 class="client-team">{{ loginUser.teamName }}Operation Springboard HUB Team</h2>

        <div class="client-info">
          <p class="label">New Client Registration</p>
          <div class="credentials">
            <p class="id">
              Client ID:
              <span class="value">{{ newCode }} </span>
              <InfoButton>
                The <span class="value">Client ID</span> is to help the facilitator to identify a specific client on
                your client list. It also allows the client in the justice system to access the self-directed client
                portal.
              </InfoButton>
            </p>
            <p class="password">
              Client Portal Password:
              <span class="value">{{ password }}</span>
              <InfoButton>
                The <span class="value">Password</span> is auto-generated and encrypted. It allows the client in the
                justice system to access the self-directed client portal.
              </InfoButton>
            </p>
          </div>
        </div>

        <div class="close-btn hub-close-normal clickable" @click="goBackList" @keypress.enter="goBackList">
          <inline-svg :src="$assets.svg['icon-close']" width="100%" height="100%" />
        </div>
      </header>

      <section class="content">
        <p class="title">Select an avatar</p>
        <ul class="avatar-selection">
          <li
            v-for="(avatar, index) in avatarList"
            :key="`${avatar.meta_code}-${index}`"
            class="avator-icon-item"
            @click="selectAvatar(avatar)"
            @keypress.enter="selectAvatar(avatar)"
          >
            <div
              :class="{
                'svg-box': true,
                clickable: true,
                active: selectedAvatar.id === avatar.id,
              }"
              :style="{
                backgroundColor: selectedAvatar.id === avatar.id && selectedColor.name ? selectedColor.name : '#FFF',
              }"
            >
              <inline-svg
                :src="$assets.svg.avatar[avatar.meta_code]"
                width="100%"
                height="100%"
                :fill="selectedAvatar.id === avatar.id && selectedColor.id ? '#FFF' : '#6D6E71'"
              />
            </div>
          </li>
        </ul>

        <p class="title">Select a color</p>
        <ul class="color-selection">
          <li
            v-for="(avatar, index) in avatarColorList"
            :key="`${avatar.meta_code}-${index}`"
            class="avator-color-item"
          >
            <div
              :class="{
                'color-ball': true,
                clickable: true,
                active: selectedColor.id === avatar.id,
              }"
              :style="{
                backgroundColor: avatar.meta_value,
              }"
              @click="selectColor(avatar)"
              @keypress.enter="selectColor(avatar)"
            />
          </li>
        </ul>

        <ul class="client-selectors">
          <li>
            <Selector aria-label="gender list" :optionList="genderList" :onChange="selectGender" />
          </li>
          <li>
            <Selector aria-label="ethno list" :optionList="ethnoList" :onChange="selectEthno" />
          </li>
          <li>
            <Selector aria-label="age list" :optionList="ageList" :onChange="selectAge" />
          </li>
        </ul>

        <a id="referralAnchor" ref="referralAnchor"><span style="display: none">referral anchor</span></a>

        <div class="referral-path" v-if="referralAvailable">
          <h1>Referral Path</h1>
          <h2>Select a Referral Path</h2>

          <ul class="referral-list">
            <li
              v-for="referral in referralList"
              :key="referral.id"
              :class="{
                'referral-wrap': true,
                'hub-link': true,
                active: selectedReferral && selectedReferral.id === referral.id,
              }"
              @click="selectedReferral = referral"
              @keypress.enter="selectedReferral = referral"
            >
              <div class="border" />
              <div class="cover" />
              <p>{{ referral.name }}</p>
              <div
                :class="{
                  circle: true,
                  'circle-green': selectedReferral && selectedReferral.id === referral.id,
                }"
              />
            </li>

            <li class="referral-wrap space-holder" v-if="referralList.length % 2 !== 0">
              <div class="border" />
              <p>&nbsp;</p>
            </li>
          </ul>
        </div>
      </section>

      <section class="footer">
        <span
          @click="showConfirmation = true"
          @keypress.enter="showConfirmation = true"
          :class="{
            'add-client-btn': true,
            'hub-grey': true,
            enabled: formComplete,
            clickable: formComplete,
          }"
          >Add Client</span
        >
      </section>

      <ClientModal v-if="showConfirmation" width="521px" height="251px" @close="showConfirmation = false">
        <h1 class="modal-header">New Client Registration</h1>
        <p class="modal-description">
          You are about to create a new client. Please take a moment to copy the password to a safe location. The
          password will be encrypted once the client profile is registered. For security purpose, our system will not be
          able to decrypt the password for you.
        </p>
        <button type="button" class="confirm-button" @click="addClient">CONFIRM / REGISTER</button>
      </ClientModal>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, onBeforeMount, computed, watch } from 'vue';
import { useRouter } from 'vue-router';

import $assets from '@/assets';
import ClientModal from '@/components/client/ClientModal.vue';
import Selector from '@/components/common/DropdownSelector.vue';
import InfoButton from '@/components/common/InfoButton.vue';
import ClientService from '@/services/client';
import MetaService from '@/services/meta';
import useMetaStore from '@/stores/meta';
import useSessionStore from '@/stores/session';

const $router = useRouter();
const MetaStore = useMetaStore();
const SessionStore = useSessionStore();
const newCode = ref(null);
const password = ref(null);
const selectedAvatar = ref({});
const selectedColor = ref({});
const selectedGender = ref({});
const selectedEthno = ref({});
const selectedAge = ref({});
const selectedReferral = ref({});

const referralAnchor = ref(null);
const showConfirmation = ref(false);

onBeforeMount(async () => {
  const codeResult = await ClientService.getNewCode();
  newCode.value = codeResult.record;

  const passwordResult = await ClientService.getNewPassword();
  password.value = passwordResult.record;

  const genderData = await MetaService.getMetasByType('gender');
  MetaStore.setGenderList(genderData.records);

  const ethnoData = await await MetaService.getMetasByType('ethno');
  MetaStore.setEthnoList(ethnoData.records);

  const ageData = await MetaService.getMetasByType('age');
  MetaStore.setAgeList(ageData.records);

  const referralData = await MetaService.getMetasByType('referral_path');
  MetaStore.setReferralList(referralData.records);

  const avatarData = await MetaService.getMetasByType('avatar');
  MetaStore.setAvatarList(avatarData.records);

  const avatarColorData = await MetaService.getMetasByType('avatar_color');
  MetaStore.setAvatarColorList(avatarColorData.records);
});

function selectAvatar(avatar) {
  selectedAvatar.value = selectedAvatar.value.id === avatar.id ? {} : { id: avatar.id, name: avatar.meta_value };
}
function selectColor(color) {
  selectedColor.value = selectedColor.value.id === color.id ? {} : { id: color.id, name: color.meta_value };
}
function selectGender(data) {
  selectedGender.value = data;
}
function selectEthno(data) {
  selectedEthno.value = data;
}
function selectAge(data) {
  selectedAge.value = data;
}

const loginUser = computed(() => {
  return SessionStore.loginUser;
});

const ageGroup = computed(() => {
  if (selectedAge.value && selectedAge.value.id > 0) {
    return selectedAge.value.name === '24+' ? 'adult' : 'youth';
  }

  return null;
});

const referralAvailable = computed(() => {
  return (
    selectedAvatar.value.id &&
    selectedColor.value.id &&
    selectedGender &&
    selectedGender.value.id > 0 &&
    selectedEthno &&
    selectedEthno.value.id > 0 &&
    ageGroup.value !== null
  );
});

const formComplete = computed(() => {
  return referralAvailable.value && selectedReferral;
});
const genderList = computed(() => {
  return [
    { id: 0, name: 'Gender Identity' },
    ...MetaStore.genderList.map((meta) => ({ id: meta.id, name: meta.meta_value })),
  ];
});
const ethnoList = computed(() => {
  return [
    { id: 0, name: 'Ethnic Identity' },
    ...MetaStore.ethnoList.map((meta) => ({ id: meta.id, name: meta.meta_value })),
  ];
});
const ageList = computed(() => {
  return [{ id: 0, name: 'Age' }, ...MetaStore.ageList.map((meta) => ({ id: meta.id, name: meta.meta_value }))];
});
const referralList = computed(() => {
  return MetaStore.referralList
    .filter((referral) => (ageGroup.value === 'youth' ? referral.meta_mark !== 'adult' : true))
    .map((meta) => ({ id: meta.id, name: meta.meta_value }));
});
const avatarList = computed(() => {
  return MetaStore.avatarList;
});
const avatarColorList = computed(() => {
  return MetaStore.avatarColorList;
});

function goBackList() {
  $router.push(SessionStore.HUBSettings.levelUp);
}

async function addClient() {
  const clientData = {};

  if (formComplete.value) {
    clientData.code = newCode.value;
    clientData.password = password.value;
    clientData.teamId = loginUser.value.team.id;
    clientData.referralId = selectedReferral.value.id;
    clientData.referralValue = selectedReferral.value.name;
    clientData.genderId = selectedGender.value.id;
    clientData.genderValue = selectedGender.value.name;
    clientData.ethnoId = selectedEthno.value.id;
    clientData.ethnoValue = selectedEthno.value.name;
    clientData.ageId = selectedAge.value.id;
    clientData.ageValue = selectedAge.value.name;
    clientData.avatarId = selectedAvatar.value.id;
    clientData.avatarValue = selectedAvatar.value.name;
    clientData.avatarColorId = selectedColor.value.id;
    clientData.avatarColorValue = selectedColor.value.name;
    clientData.facilitatorId = loginUser.value.id;

    await ClientService.addClient(clientData);
    goBackList();
  }
}

watch(referralAvailable, (val) => {
  if (val) {
    nextTick(() => {
      referralAnchor.value.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    });
  } else {
    selectedReferral.value = null;
  }
});
</script>

<style scoped>
.page-wrapper {
  width: 100%;
  height: 100%;
}
.client-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3em 1em;
}

.top {
  position: relative;
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
}
/** Close button */
.top .close-btn {
  position: absolute;
  right: 0;
  top: -1.4em;
  width: 2.8em;
  height: 2.8em;
}

.top .client-team {
  display: flex;
  justify-content: center;
  font-size: 1.4em;
  text-align: center;
  margin: 2em 0 0;
}
.top > h1 {
  margin: 1em 0 0;
  text-align: center;
}
.top .client-info .label,
.client-info .credentials {
  display: inline-block;
}
.top .client-info .label {
  width: 5em;
  padding: 0.1em 2.5em;
  color: white;
  background-color: #8dc54e;
  border-top-left-radius: 2em;
  border-bottom-left-radius: 2em;
}
.top .credentials {
  margin-left: 0.5em;
}
.client-info .credentials > p {
  margin: 0.25em 0;
}
.client-info .credentials .value {
  font-weight: bold;
}
.top .identify {
  margin: 2em 0 0;
  text-align: center;
}

.content {
  width: 100%;
  font-size: 1.2em;
  text-align: center;
}
.content p.title {
  margin-top: 3em;
}

/** Avatars */
.content .avatar-selection {
  display: grid;
  grid-template-columns: repeat(auto-fit, 6em);
  row-gap: 1em;
  justify-content: space-evenly;
  margin-top: 2em;
}
.avator-icon-item {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 6em;
  height: 6em;
}
.avator-icon-item .svg-box {
  width: 3em;
  height: 3em;
  border-radius: 2.5em;
  border: 1px solid #a7a9ac;
  padding: 1em;
}

.avator-icon-item .svg-box:hover,
.avator-icon-item .svg-box.active {
  width: 3.6em;
  height: 3.6em;
  border-radius: 3em;
  box-shadow: 0 0 1em 0 #a7a9ac;
  padding: 1.2em;
}

/** Color balls */
.content .color-selection {
  display: grid;
  grid-template-columns: repeat(auto-fit, 4em);
  row-gap: 1em;
  justify-content: space-evenly;
  margin-top: 2em;
}
.avator-color-item {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 4em;
  height: 4em;
}
.avator-color-item .color-ball {
  width: 3em;
  height: 3em;
  border-radius: 1.5em;
}
.avator-color-item .color-ball:hover,
.avator-color-item .color-ball.active {
  width: 3.6em;
  height: 3.6em;
  border-radius: 1.8em;
  box-shadow: 0 0 0.4em 0 #a7a9ac;
}

/** Info selectors */
.client-selectors {
  display: grid;
  grid-template-columns: repeat(auto-fit, 100%);
  row-gap: 2em;
  margin-top: 3em;
  font-size: 0.9em;
}

/** Referral selector */
.referral-path {
  margin-top: 3em;
}
.referral-path > h1,
.referral-path > h2 {
  margin: 0 0 0.5em;
}
.referral-list {
  margin-top: 2em;
}
.referral-wrap.space-holder {
  display: none;
}
.referral-wrap {
  display: grid;
  grid-template-columns: 1fr 3em;
  align-items: center;
  position: relative;

  box-sizing: border-box;
  padding: 0 0.5em;
}
.referral-wrap .border {
  position: absolute;
  left: 0;
  top: 0;
  width: calc(100% - 1px);
  height: calc(100% - 1px);
  border-top: 1px solid #a7a9ac;
  border-bottom: 1px solid #a7a9ac;
}
.referral-wrap .cover {
  position: absolute;
  left: -0.4em;
  top: -1px;
  width: calc(100% + 0.6em);
  height: calc(100%);

  border-radius: 0.5em;
  border: 1px solid #a7a9ac;
  background: #fff;
  box-shadow: 0 0 0.8em 0 #a7a9ac;
  opacity: 0;
  transition: opacity 0.3s;
}
.referral-wrap:hover .cover {
  opacity: 1;
}
.referral-wrap > p {
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  margin: 0;
  min-height: 2em;
  padding: 0.5em 0;
  font-size: 0.9em;
  text-align: left;
}

.referral-wrap .circle {
  position: relative;
  margin-left: 1.8em;
  width: 1em;
  height: 1em;
  border-radius: 0.5em;
  background: #a7a9ac;
}
.referral-wrap .circle-green {
  background: #8ac640;
}

/** Form buttons */
.footer {
  display: flex;
  justify-content: center;
  margin-top: 4em;
  width: 100%;
}
.add-client-btn {
  max-width: 12em;
  width: 80%;
  padding: 0.5em 0;
  border-radius: 0.4em;
  border: 1px solid #a7a9ac;
  font-size: 1.2em;
  text-align: center;
}
.add-client-btn.enabled {
  background: #8ac640;
  color: #ffffff;
}
.add-client-btn.enabled:hover {
  box-shadow: 0 0 5px 0 #a7a9ac;
}

.modal .modal-header {
  margin-top: 0;
}
.modal .modal-description {
  color: #000000;
  margin-bottom: 79px;
}
.modal .confirm-button {
  height: 42px;
  width: 210px;

  background: #62a517;
  border: none;
  border-radius: 9px;

  color: #ffffff;
  text-align: center;

  font-family: 'LatoRegular';
  font-weight: 700;
  font-size: 15px;

  cursor: pointer;
}

/** Landscape mobile */
@media only screen and (orientation: landscape) and (max-height: 600px) {
  .content .avatar-selection {
    grid-template-columns: repeat(auto-fit, 5em);
  }
  .avator-icon-item {
    width: 5em;
    height: 5em;
  }
  .avator-icon-item .svg-box {
    width: 2em;
    height: 2em;
    border-radius: 2em;
  }

  .avator-icon-item .svg-box:hover,
  .avator-icon-item .svg-box.active {
    width: 3em;
    height: 3em;
    border-radius: 2.5em;
    padding: 1em;
  }
}

/** Larger devices */
@media only screen and (min-width: 601px) and (min-height: 601px) {
  .client-form {
    margin: 0 auto;
    max-width: 64em;
    padding: 3em 2em;
  }

  .top .close-btn {
    position: absolute;
    right: 0;
    top: 0;
    width: 3em;
    height: 3em;
  }

  .top .client-team {
    margin: 0 0 2em;
    text-align: left;
  }

  .content .avatar-selection {
    grid-template-columns: repeat(auto-fit, 7em);
  }
  .avator-icon-item {
    width: 7em;
    height: 7em;
  }
  .avator-icon-item .svg-box {
    width: 3em;
    height: 3em;
    border-radius: 2.8em;
    padding: 1.3em;
  }

  .avator-icon-item .svg-box:hover,
  .avator-icon-item .svg-box.active {
    width: 4em;
    height: 4em;
    border-radius: 3.5em;
    padding: 1.5em;
  }

  .content .color-selection {
    padding: 0 10%;
  }

  .client-selectors {
    grid-template-columns: repeat(auto-fit, 30%);
    justify-content: space-evenly;
    margin-top: 4em;
  }

  .referral-list {
    display: grid;
    grid-template-columns: repeat(2, 45%);
    column-gap: 2em;
    justify-content: center;
  }
  .referral-wrap.space-holder {
    display: grid;
  }
}
</style>
